/*
 * Payment details: pure response shaping, wording and error routing, pinned to
 * teacher_payment_details.json by adapters.test.js.
 */
import { EM_DASH, api_error, month_label } from '../format';
import { with_ist } from '../shared/ist';
import { reason_length } from '../needs_attention/params';
import { BANK_DETAILS_FORBIDDEN_TEXT } from '../shared/bank_access';
import { parse_csv } from '../statement/logic';

const str = (v) => (typeof v === 'string' ? v : '');
const shown = (v) => str(v) || EM_DASH;
const yes_no = (v) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  return EM_DASH;
};
const pct = (v) => (str(v) ? `${str(v)}%` : EM_DASH);

export const CODE_NOT_PENDING = 'PAYMENT_DETAILS_NOT_PENDING';
export const CODE_SHARED_CONFIRM = 'SHARED_ACCOUNT_CONFIRM_REQUIRED';
export const CODE_NOT_FOUND = 'PAYMENT_DETAILS_NOT_FOUND';
export const REASON_MIN_LEN = 5;
export const REASON_MAX_LEN = 512;

export const STALE_TEXT = 'These details changed after you opened this. Close and reload.';
// Every 403 here is the payments-role gate; the server's wording is replaced by one clear line.
export const FORBIDDEN_TEXT = BANK_DETAILS_FORBIDDEN_TEXT;

// holder_name_match: a hint for Ops, never a refusal.
const NAME_MATCH = {
  MATCH: { label: 'Name matches profile', tone: 'green' },
  PARTIAL: { label: 'Name partly matches profile', tone: 'orange' },
  DIFFERENT: { label: 'Name differs from profile', tone: 'red' }
};

export const name_match_view = (code) =>
  NAME_MATCH[code] || { label: 'Name not checked', tone: 'gray' };

const ACCOUNT_TYPES = { SAVINGS: 'Savings', CURRENT: 'Current' };
const STATUSES = {
  PENDING: { label: 'Waiting for review', tone: 'orange' },
  APPROVED: { label: 'Approved', tone: 'green' },
  REJECTED: { label: 'Rejected', tone: 'red' },
  SUPERSEDED: { label: 'Replaced', tone: 'gray' }
};

export const status_view = (code) =>
  STATUSES[code] || { label: str(code) || EM_DASH, tone: 'gray' };

const teacher_of = (t) => ({
  teacher_uuid: str(t && t.teacher_uuid),
  teacher_name: str(t && t.teacher_name) || EM_DASH,
  teacher_email: str(t && t.teacher_email)
});

// GET /payout_details/pending: oldest first; next_after_id null = the last page.
export const pending_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    ...teacher_of(r),
    detail_uuid: str(r.detail_uuid),
    submitted_at: with_ist(shown(r.submitted_at_ist)),
    account_holder_name: shown(r.account_holder_name),
    account_number_masked: shown(r.account_number_masked),
    ifsc: shown(r.ifsc),
    bank_name: shown(r.bank_name),
    branch: shown(r.branch),
    gst_registered: r.gst_registered === true,
    gst_rate: pct(r.gst_rate_pct),
    tds_rate: pct(r.tds_rate_pct),
    is_change: r.is_change === true,
    shared_account: r.shared_account === true,
    name_match: name_match_view(r.holder_name_match)
  }));
  const next = typeof d.next_after_id === 'number' && d.next_after_id > 0 ? d.next_after_id : null;
  return { rows, next_after_id: next };
};

// The compared fields, in reading order; `changed` is the key changed_fields uses (null = not compared).
const FIELDS = [
  { key: 'account_holder_name', changed: 'account_holder_name', label: 'Account holder name' },
  { key: 'bank_file_name', changed: null, label: 'Name in the bank file' },
  { key: 'account_number', changed: 'account_number', label: 'Account number', mono: true },
  { key: 'ifsc', changed: 'ifsc', label: 'IFSC' },
  { key: 'bank_name', changed: null, label: 'Bank' },
  { key: 'branch', changed: null, label: 'Branch' },
  { key: 'account_type', changed: 'account_type', label: 'Account type' },
  { key: 'pan', changed: 'pan', label: 'PAN', mono: true },
  { key: 'pan_aadhaar_linked', changed: 'pan_aadhaar_linked', label: 'PAN linked to Aadhaar' },
  { key: 'gst_registered', changed: 'gst_registered', label: 'GST registered' },
  { key: 'gstin', changed: 'gstin', label: 'GSTIN', mono: true },
  { key: 'takes_input_credit', changed: 'takes_input_credit', label: 'Takes input tax credit' },
  { key: 'gst_rate_pct', changed: 'gst_rate_pct', label: 'GST rate' },
  { key: 'tds_rate_pct', changed: 'tds_rate_pct', label: 'TDS rate' }
];

// The full value when the server sent it (Ops view, audited server-side), else the masked one.
const full_or_masked = (full, masked) => str(full) || shown(masked);

// One side (submission or the details in use) as display strings; null side = no approved details.
const side_values = (d) => {
  if (!d) return null;
  return {
    account_holder_name: shown(d.account_holder_name),
    bank_file_name: shown(d.bank_file_name),
    account_number: full_or_masked(d.account_number_full, d.account_number_masked),
    ifsc: shown(d.ifsc),
    // The server's own IFSC lookup at submit; no branch = saved before branches were, or Razorpay was down.
    bank_name: shown(d.bank_name),
    branch: shown(d.branch),
    account_type: ACCOUNT_TYPES[d.account_type] || shown(d.account_type),
    pan: full_or_masked(d.pan_full, d.pan_masked),
    pan_aadhaar_linked: yes_no(d.pan_aadhaar_linked),
    gst_registered: yes_no(d.gst_registered),
    gstin: full_or_masked(d.gstin_full, d.gstin_masked),
    takes_input_credit: yes_no(d.takes_input_credit),
    gst_rate_pct: pct(d.gst_rate_pct),
    tds_rate_pct: pct(d.tds_rate_pct)
  };
};

export const FULL_SHOWN_TEXT =
  'Full details are shown to the payments team. This view was recorded.';
export const FULL_UNAVAILABLE_TEXT =
  "Full details can't be shown right now. Showing masked values.";

// full_details: anything but SHOWN (incl. an older server that omits it) means masked values are on screen.
export const full_details_notice = (code) =>
  code === 'SHOWN'
    ? { text: FULL_SHOWN_TEXT, warn: false }
    : { text: FULL_UNAVAILABLE_TEXT, warn: true };

// A rejection as Ops read it (list row or pop-up): the reason the teacher sees, who and when (IST).
const rejection_of = (reason, by, at) => ({
  reason: shown(reason),
  by: shown(by),
  at: with_ist(shown(at))
});

// awaiting row last_rejection; null = none (or an older server that omits it).
export const rejection_view = (r) => {
  if (!r || typeof r !== 'object') return null;
  return { detail_uuid: str(r.detail_uuid), ...rejection_of(r.reason, r.by_name, r.at_ist) };
};

// The byline under a reason: "by Ops One · 2026-10-14 11:30:00 IST"; an unknown part drops out.
export const rejection_byline = (rej) =>
  [rej.by !== EM_DASH ? `by ${rej.by}` : '', rej.at !== EM_DASH ? rej.at : '']
    .filter(Boolean)
    .join(' · ');

// GET /payout_details/view: field rows side by side, the changed ones flagged for highlighting.
export const detail_view = (data) => {
  const d = data || {};
  const sub = d.submission || {};
  const cur = d.current_approved || null;
  const changed = new Set(Array.isArray(d.changed_fields) ? d.changed_fields : []);
  const sub_vals = side_values(sub);
  const cur_vals = side_values(cur);
  const shared = (Array.isArray(d.shared_with) ? d.shared_with : []).map((t) => ({
    teacher_uuid: str(t && t.teacher_uuid),
    teacher_name: str(t && t.teacher_name) || EM_DASH
  }));
  return {
    teacher: teacher_of(d.teacher),
    detail_uuid: str(sub.uuid),
    ifsc: str(sub.ifsc),
    status: status_view(sub.status),
    submitted_at: with_ist(shown(sub.submitted_at_ist)),
    reviewed_at: with_ist(str(sub.reviewed_at_ist)),
    review_note: str(sub.review_note),
    rejection:
      sub.status === 'REJECTED'
        ? rejection_of(sub.review_note, sub.reviewed_by_name, sub.reviewed_at_ist)
        : null,
    has_current: cur !== null,
    current_uuid: cur ? str(cur.uuid) : '',
    rows: FIELDS.map((f) => ({
      key: f.key,
      label: f.label,
      mono: f.mono === true,
      submission: sub_vals[f.key],
      current: cur_vals ? cur_vals[f.key] : EM_DASH,
      changed: cur !== null && f.changed !== null && changed.has(f.changed)
    })),
    changed_count: cur === null ? 0 : changed.size,
    name_match: name_match_view(d.holder_name_match),
    shared_with: shared,
    can_review: d.can_review === true,
    full_notice: full_details_notice(d.full_details)
  };
};

// GET /payout_details/awaiting.
export const awaiting_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    ...teacher_of(r),
    latest_month: shown(r.latest_month),
    pending_detail_uuid: str(r.pending_detail_uuid),
    rejection: str(r.pending_detail_uuid) ? null : rejection_view(r.last_rejection)
  }));
  const next =
    typeof d.next_after_teacher_id === 'number' && d.next_after_teacher_id > 0
      ? d.next_after_teacher_id
      : null;
  return { since_month: str(d.since_month), rows, next_after_teacher_id: next };
};

// An awaiting row's state: waiting beats an older rejection (the teacher has sent new details).
export const awaiting_status = (r) => {
  if (r.pending_detail_uuid) return { label: 'Submitted, waiting for review', tone: 'orange' };
  if (r.rejection) return { label: 'Rejected', tone: 'red' };
  return { label: 'Not submitted yet', tone: 'gray' };
};

const HISTORY_ACTIONS = {
  APPROVED: { label: 'Approved', tone: 'green' },
  REJECTED: { label: 'Rejected', tone: 'red' }
};

// GET /payout_details/review_history: newest first; approvals carry no reason.
export const history_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    id: typeof r.id === 'number' ? r.id : 0,
    detail_uuid: str(r.detail_uuid),
    teacher_uuid: str(r.teacher_uuid),
    teacher_name: str(r.teacher_name) || EM_DASH,
    action: HISTORY_ACTIONS[r.action] || { label: shown(r.action), tone: 'gray' },
    action_code: str(r.action),
    reason: str(r.reason),
    by: shown(r.by_name),
    at: with_ist(shown(r.at_ist))
  }));
  return { pay_month: str(d.pay_month), rows, truncated: d.truncated === true };
};

// The review history's count line; null until loaded.
export const history_summary = ({ list, pay_month }) => {
  if (!list || list.status !== 'ready') return null;
  const n = list.rows.length;
  const rejected = list.rows.filter((r) => r.action_code === 'REJECTED').length;
  return {
    lead: `${n} ${n === 1 ? 'decision' : 'decisions'}`,
    rest: ` in ${pay_month}: ${n - rejected} approved, ${rejected} rejected.${
      list.meta && list.meta.truncated ? ' Showing the newest 500 only.' : ''
    }`
  };
};

// POST approve: the follow-up the toast states (recalc_months: OPEN months flagged after a rate change).
export const approve_done_text = (data, teacher_name) => {
  const months = Array.isArray(data && data.recalc_months) ? data.recalc_months : [];
  const who = teacher_name && teacher_name !== EM_DASH ? ` for ${teacher_name}` : '';
  const tail = months.length
    ? ` The new tax rates apply from now; ${months.join(', ')} will be recalculated.`
    : '';
  return `Payment details approved${who}.${tail}`;
};

export const reject_done_text = (teacher_name) =>
  teacher_name && teacher_name !== EM_DASH
    ? `Payment details rejected for ${teacher_name}. The teacher sees your reason.`
    : 'Payment details rejected. The teacher sees your reason.';

export const reject_reason_error = (raw) => {
  const n = reason_length(raw);
  if (n === 0) return 'Write the reason the teacher will see.';
  if (n < REASON_MIN_LEN) return `The reason must be at least ${REASON_MIN_LEN} characters.`;
  if (n > REASON_MAX_LEN) return `The reason must be at most ${REASON_MAX_LEN} characters.`;
  return '';
};

export const review_error = (e, fallback) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (status === 401) return { kind: 'auth', text: message || fallback };
  if (status === 403 || code === 'FORBIDDEN') {
    return { kind: 'forbidden', text: status === 403 ? FORBIDDEN_TEXT : message || FORBIDDEN_TEXT };
  }
  if (code === CODE_NOT_PENDING) return { kind: 'stale', text: STALE_TEXT };
  if (code === CODE_SHARED_CONFIRM) return { kind: 'shared', text: message || fallback };
  if (code === CODE_NOT_FOUND) return { kind: 'not_found', text: message || fallback };
  return { kind: 'inline', text: message || fallback };
};

const SINCE_MONTH_RE = /^20[0-9]{2}-(0[1-9]|1[0-2])$/;

// '' = the server's default; anything else must look like 2026-10 (the server's INVALID_PAY_MONTH).
export const since_month_error = (raw) => {
  const v = str(raw).trim();
  if (!v || SINCE_MONTH_RE.test(v)) return '';
  return 'Enter the month as YYYY-MM, for example 2026-10.';
};

// The month to apply once typing pauses, or null: an invalid or unchanged draft applies nothing.
export const since_filter_next = (draft, applied) => {
  if (since_month_error(draft)) return null;
  const v = str(draft).trim();
  return v === str(applied) ? null : v;
};

export const since_filters_on = (since_month) => !!str(since_month);

// The line under the awaiting filter: who the list shows, in words; null until it has loaded.
export const awaiting_filter_summary = ({ list, since_month }) => {
  if (!list || list.status !== 'ready') return null;
  const n = list.rows.length;
  const noun = n === 1 && !list.next ? 'teacher' : 'teachers';
  const month = (list.meta && list.meta.since_month) || str(since_month);
  const since = month
    ? ` with a payment month since ${month}${since_filters_on(since_month) ? '' : ' (the default)'}`
    : '';
  return {
    lead: `${n}${list.next ? '+' : ''} ${noun}`,
    rest: `${since} and no approved payment details.`
  };
};

// The review month: required (no "default"), YYYY-MM.
export const review_month_error = (raw) =>
  str(raw).trim() ? since_month_error(raw) : 'Enter the month as YYYY-MM, for example 2026-10.';

// The dialog's note on where the decision is logged (the page's month); '' when none is set.
export const audit_month_text = (pay_month) =>
  SINCE_MONTH_RE.test(str(pay_month))
    ? `Your decision is logged in the ${month_label(pay_month)} audit trail.`
    : '';

// ---- full-details download ----

// ops enums export_unavailable (pinned in adapters.test): the account number, PAN and GSTIN of an unopened row.
export const EXPORT_UNAVAILABLE = 'UNAVAILABLE';

// Full-details CSV text -> rows whose Account number reads UNAVAILABLE (so a name reading it never counts).
export const export_unavailable_count = (csv_text) => {
  const [header, ...rows] = parse_csv(csv_text);
  if (!header) return 0;
  const col = header.indexOf('Account number');
  const unavailable = (r) =>
    col >= 0 ? r[col] === EXPORT_UNAVAILABLE : r.includes(EXPORT_UNAVAILABLE);
  return rows.filter(unavailable).length;
};

// The warning after a download with n UNAVAILABLE rows; '' when there are none.
export const export_unavailable_text = (n) => {
  if (!(n > 0)) return '';
  const rows = n === 1 ? '1 row' : `${n} rows`;
  return `${rows} could not be opened, so their account number, PAN and GSTIN show UNAVAILABLE. Download again; if they still show UNAVAILABLE, ask those teachers to re-enter their payment details.`;
};
